import { useEffect, useRef, useState } from "react";
import { X, Upload } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, pick } from "@/i18n";
import { adminFetch } from "@/utils/adminFetch";

export default function ImagePicker({ onSelect, onClose }) {
  const { lang } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery-photos`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPhotos)
      .catch(() => {});
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminFetch("/admin/upload", { method: "POST", body: fd });
      onSelect(r.url);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="image-picker">
      <div className="bg-ivory max-w-3xl w-full max-h-[80vh] flex flex-col border border-stone2/60" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone2/50">
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/60">Scegli o carica una foto</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="image-picker-upload"
              className="inline-flex items-center gap-2 bg-medblue text-ivory text-[10px] font-medium uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-ink disabled:opacity-40 transition-colors duration-300"
            >
              <Upload size={13} strokeWidth={1.5} /> {uploading ? "..." : "Carica foto"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="image-picker-file" />
            <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="image-picker-close" className="text-ink/50 hover:text-ink transition-colors">
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto p-6 grid grid-cols-3 sm:grid-cols-4 gap-3">
          {photos.map((p) => (
            <button
              key={p.photo_id}
              type="button"
              onClick={() => onSelect(p.src)}
              data-testid={`image-picker-photo-${p.photo_id}`}
              className="group relative aspect-[4/3] overflow-hidden border border-stone2/40 hover:border-medblue transition-colors duration-300"
            >
              <img src={p.src} alt={pick(p.title, lang)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <span className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-[9px] text-ivory truncate">{pick(p.title, lang)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
